// APShowPage — the v4 detail-page shell (contract § Related patterns):
// back + title + meta badges + actions header, then sectioned content.
// An embedded related collection is just
// `<APCollection resource=… permanentFilter={…} urlSync={false} view=…/>`
// inside an APShowSection — no hand-rolled show-page tables
// (GroupShow: 519 lines → composition).
import { AltArrowLeftLinear } from '@assessprep/icons/solar';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';

export interface APShowPageProps {
  /** Already-translated page title. */
  title: ReactNode;
  /** Meta badges beside the title (status pills etc.). */
  badges?: ReactNode;
  /** Already-translated muted line under the title. */
  subTitle?: ReactNode;
  /** End-aligned header actions (edit / delete / …). */
  actions?: ReactNode;
  /** Back affordance — typically `() => navigate('/…')`. */
  onBack: () => void;
  /** Skeleton header + body while the record loads. */
  loading?: boolean;
  /** Sections — compose with APShowSection. */
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const APShowPage = ({
  title,
  badges,
  subTitle,
  actions,
  onBack,
  loading = false,
  children,
  className,
  'data-testid': testId = 'ap-show-page',
}: APShowPageProps) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className={cn('space-y-6', className)} data-testid={`${testId}-skeleton`}>
        <div className="flex items-center gap-2">
          <Skeleton className="size-7" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)} data-testid={testId}>
      <div className="flex flex-wrap items-start gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('CommonText.back')}
          onClick={onBack}
          data-testid={`${testId}-back`}
        >
          <AltArrowLeftLinear aria-hidden="true" className="rtl:rotate-180" />
        </Button>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1
              className="text-2xl leading-none font-semibold tracking-tight break-words"
              data-testid={`${testId}-title`}
            >
              {title}
            </h1>
            {badges && <div className="flex flex-wrap items-center gap-1">{badges}</div>}
          </div>
          {subTitle && <p className="text-sm text-muted-foreground">{subTitle}</p>}
        </div>
        {actions && (
          <div className="flex items-center gap-2" data-testid={`${testId}-actions`}>
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

export interface APShowSectionProps {
  /** Already-translated section heading; omit for an unlabeled block. */
  title?: ReactNode;
  /** End-aligned section-level actions. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const APShowSection = ({
  title,
  actions,
  children,
  className,
  'data-testid': testId = 'ap-show-section',
}: APShowSectionProps) => (
  <section className={cn('space-y-3', className)} data-testid={testId}>
    {(title || actions) && (
      <div className="flex items-center gap-2">
        {title && <h2 className="text-base font-semibold">{title}</h2>}
        {actions && <div className="ms-auto flex items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);
